import { Alert, Button, ButtonAsLink, Container, Link } from '@cluey/cluey-components';
import moment from 'moment';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { cancelEnrolment } from '../../../actions/hub/hubCancelActions';
import BackArrow from '../../../assets/images/hubContact/icon-filled-arrow-left.svg';
import withHeadTag from '../../../common/HeadComponent';
import InPageLoader from '../../../common/InPageLoader';
import PageError from '../../../common/PageError';
import BasePageLayout from '../../../templates/BasePageLayout/BasePageLayout';
import { CancelEnrolmentParams } from '../../../types/hubInterfaces';
import { PricePlan } from '../../../types/hubTypes';
import { scrollToTop } from '../../../util/helpers';
import { PATH_HUB_PLAN_DETAILS } from '../../../util/pagePath';
import { useSetPageHeader } from '../../../v2/app-shell/usePageHeader';
import PaymentPolicyFAQ from '../components/PaymentPolicyFAQ';
import PricingPlanComparison from './components/PricingPlanComparison';
import useChangePricingPlanContext, { ChangePricingPlanSteps } from './useChangePricingPlanContext';

interface Props {
  interrupting?: boolean;
  cancelEnrolmentPayload?: CancelEnrolmentParams & {
    onSubmitSuccess(): void;
    onSubmitFailed?(): void;
  };
  onGoBack(): void;
}

interface CancelEnrolmentInterruptionLayoutProps {
  loading: boolean;
  isError: boolean;
  cancelEnrolmentPayload: CancelEnrolmentParams & {
    onSubmitSuccess(): void;
    onSubmitFailed?(): void;
  };
  onGoBack(): void;
}

export function getValueMaxPlan(allowedPlansForTransition: PricePlan[]) {
  return allowedPlansForTransition.find((plan) => plan.pricingPlanDisplayName === 'Value Max');
}

const CancelEnrolmentInterruptionLayout = ({
  loading,
  isError,
  cancelEnrolmentPayload,
  onGoBack,
}: CancelEnrolmentInterruptionLayoutProps) => {
  const dispatch = useDispatch();
  const { currentPricingPlan, allowedPlansForTransition, setSelectedPlan, setCurrentStep, setMaxHeights } =
    useChangePricingPlanContext();
  const valueMaxPlan = getValueMaxPlan(allowedPlansForTransition);
  if (loading) {
    return <InPageLoader position="relative" height="auto" bgClass="bg-transparent" />;
  } else if (isError) {
    return <PageError activePage="hub-page" retryEnabled={false} />;
  }
  return (
    <BasePageLayout>
      <Container className="!px-0">
        <div className="w-full">
          <ButtonAsLink className="mx-4 mb-5 p-0 lg:mx-0" onClick={onGoBack}>
            <img src={BackArrow} alt="back" />
          </ButtonAsLink>
        </div>
        <div className="rounded-lg border border-grey-3 p-10 sm:p-20">
          <div className="mb-10 text-grey-6 lg:mx-0">
            <div className="text-center">
              <b className="mb-6 font-display text-3xl">{`If your current price isn't working for you, would $${valueMaxPlan?.privatePriceTier?.costPerSession} per session work better?`}</b>
              <div className="mb-10 mt-4 flex items-center justify-center gap-x-3">
                <span className="text-lg text-grey-5">
                  Our Value Max plans get you the same quality sessions with savings on every session
                </span>
              </div>
            </div>
            <div className="mx-auto w-[300px] rounded-lg border-2 border-blood-orange-4">
              <div className="rounded-t border-2 border-blood-orange-4 bg-blood-orange-4 p-2 text-center font-bold text-white">
                Recommended
              </div>
              <div className="p-4">
                <p className="proxima-nova mt-8 text-center text-xl font-bold text-grey-6 md:text-2xl">
                  {valueMaxPlan?.pricingPlanDisplayName}
                </p>
                <p className="proxima-nova text-center text-5xl font-bold text-grey-6">
                  <span className="align-top text-xl">$</span>
                  {valueMaxPlan?.privatePriceTier?.costPerSession}
                </p>
                <p className="proxima-nova mb-5 text-center text-base text-grey-6">Per session</p>
                <p className="proxima-nova text-center text-base text-grey-6">{valueMaxPlan?.paymentTerm}</p>
              </div>
            </div>
          </div>
          <div className="mx-auto mt-10 flex w-fit flex-col items-center">
            {currentPricingPlan?.allowTransitionTo && (
              <div className="mb-4">
                <Button
                  size="lg"
                  className="w-full md:w-auto"
                  onClick={() => {
                    setSelectedPlan(valueMaxPlan);
                    setCurrentStep(ChangePricingPlanSteps.CONFIRM_CHANGES);
                    setMaxHeights(null);
                  }}
                >
                  Yes, show me this plan
                </Button>
              </div>
            )}
            <Button
              size="lg"
              appearance="reverse"
              corners="pill"
              className="border-none"
              onClick={() => {
                dispatch(cancelEnrolment(cancelEnrolmentPayload));
              }}
            >
              Continue with cancellation request
            </Button>
          </div>
        </div>
      </Container>
    </BasePageLayout>
  );
};

const ChangePricingPlanPage = ({ interrupting, cancelEnrolmentPayload, onGoBack }: Props) => {
  const {
    loading,
    isError,
    currentPricingPlan,
    upcomingPricingPlan,
    allowedPlansForTransition,
    nextUnpaidSession,
    timezone,
    paidSessions,
  } = useChangePricingPlanContext();
  useEffect(() => {
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSetPageHeader({
    label: 'Cluey plans',
    showBackButton: true,
  });

  if (interrupting) {
    return (
      <CancelEnrolmentInterruptionLayout
        loading={loading}
        isError={isError}
        cancelEnrolmentPayload={cancelEnrolmentPayload}
        onGoBack={onGoBack}
      />
    );
  }

  return (
    <BasePageLayout>
      {loading && <InPageLoader position="relative" height="auto" bgClass="bg-transparent" />}
      {!loading && isError && <PageError activePage="hub-page" retryEnabled={false} />}
      {!loading && !isError && (
        <>
          {!!paidSessions.length && (
            <Alert
              type="info"
              className="mb-6 rounded-lg border border-blue-500 p-5 text-blue-500 [&_h4]:font-poppins"
              title={
                allowedPlansForTransition?.length > 0
                  ? `You have ${paidSessions.length} remaining paid sessions on your current ${currentPricingPlan?.pricingPlanDisplayName} plan`
                  : 'You have no pricing plans to transition to'
              }
              body={null}
            >
              <div className="mt-3">
                {upcomingPricingPlan ? (
                  <span>
                    New plan payment will only start from the next unpaid session which is on{' '}
                    <b>{moment(upcomingPricingPlan?.effectiveFromDate).tz(timezone).format('MMMM D')}</b>.
                  </span>
                ) : (
                  <span>
                    Any new plan can only start from the next unpaid session which is on{' '}
                    <b>{moment(nextUnpaidSession?.startTime).tz(timezone).format('MMMM D')}</b>.
                  </span>
                )}
              </div>
            </Alert>
          )}
          <div className="rounded-3xl border border-grey-100 bg-white p-6 shadow-md">
            <div className="mb-4 mt-6 flex md:my-6">
              <div className="flex-col">
                <div className="text-base">
                  <Link className="link !text-base" to={PATH_HUB_PLAN_DETAILS}>
                    Review your current plan
                  </Link>
                  <div className="mt-2">Change plan to fit your needs</div>
                </div>
              </div>
            </div>
            <section className="pb-10 lg:mb-0 lg:pb-[60px]">
              <PricingPlanComparison
                pricingPlans={[
                  currentPricingPlan,
                  ...allowedPlansForTransition.sort(
                    (plan1, plan2) => plan1.privatePriceTier.costPerSession - plan2.privatePriceTier.costPerSession
                  ),
                ]}
              />
            </section>
          </div>
          <div className="mb-10 mt-6 rounded-3xl border border-grey-100 bg-white px-10 py-[60px] shadow-md">
            <PaymentPolicyFAQ />
          </div>
        </>
      )}
    </BasePageLayout>
  );
};

export default withHeadTag(ChangePricingPlanPage);
